import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './EditCard.css';
import { FaLinkedin, FaFacebook, FaInstagram, FaYoutube, FaTwitter, FaGithub, FaPhoneAlt, FaEnvelope, FaVideo, FaMapMarkerAlt, FaGlobe } from 'react-icons/fa';
import { BsMicrosoftTeams } from "react-icons/bs";

const EditCard = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [cardData, setCardData] = useState({
        name: '',
        position: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        backgroundColor: '#ffffff',
        companyName: '',
        backgroundImage: '',
        uuid: '',
        qrCode: '',
        linkedin: '',
        facebook: '',
        instagram: '',
        youtube: '',
        twitter: '',
        github: '',
        zoom: '',
        teams: ''
    });
    const [error, setError] = useState(null);
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);
    const [existingBackgroundImage, setExistingBackgroundImage] = useState('');
    const [activeTab, setActiveTab] = useState('Information'); // For toggling between tabs
    const API_URL = process.env.REACT_APP_API_URL;

    const generateQRCode = useCallback(async (uuid) => {
        try {
            const url = `${process.env.REACT_APP_FRONTEND_URL}/card/${uuid}`;
            const qrCode = await QRCode.toDataURL(url);
            setCardData(prevState => ({ ...prevState, qrCode }));
        } catch (error) {
            console.error('Error generating QR code:', error);
        }
    }, []);

    useEffect(() => {
        const fetchCard = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/cards/${id}`);
                const fetchedData = response.data;
                const sanitizedData = Object.keys(fetchedData).reduce((acc, key) => {
                    acc[key] = fetchedData[key] !== null ? fetchedData[key] : '';
                    return acc;
                }, {});
                
                setCardData(sanitizedData);
                setExistingBackgroundImage(sanitizedData.backgroundImage);
                generateQRCode(sanitizedData.uuid);
            } catch (error) {
                console.error('Error fetching card:', error);
                setError('Error fetching card data');
            }
        };
        fetchCard();
    }, [id, API_URL, generateQRCode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCardData({ ...cardData, [name]: value !== null ? value : '' });
    };

    const handleBackgroundImageChange = (e) => {
        const file = e.target.files[0];
        setBackgroundImageFile(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setCardData({ ...cardData, backgroundImage: reader.result });
        };
        reader.readAsDataURL(file);
    };

    const validateURL = (name, value) => {
        // If the value is empty, skip validation
        if (!value) return true;
    
        // Validate against the regex pattern if the value is not empty
        const urlPatterns = {
            linkedin: /^https:\/\/(www\.)?linkedin\.com\/.*$/i,
            facebook: /^https:\/\/(www\.)?facebook\.com\/.*$/i,
            instagram: /^https:\/\/(www\.)?instagram\.com\/.*$/i,
            youtube: /^https:\/\/(www\.)?youtube\.com\/.*$/i,
            twitter: /^https:\/\/(www\.)?twitter\.com\/.*$/i,
            github: /^https:\/\/(www\.)?github\.com\/.*$/i,
            zoom: /^https:\/\/.*zoom\.us\/.*$/i,
            teams: /^https:\/\/.*teams\.microsoft\.com\/.*$/i,
        };
    
        return urlPatterns[name] ? urlPatterns[name].test(value) : true;
    };
    

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        // Only validate fields with non-empty values
        const isValid = Object.keys(cardData).every((key) => validateURL(key, cardData[key]));
    
        if (!isValid) {
            alert('Please enter valid URLs for the social links.');
            return;
        }
    
        // If validation passes, proceed with form submission
        const formData = new FormData();
        Object.keys(cardData).forEach(key => {
            formData.append(key, cardData[key] !== null ? cardData[key] : '');
        });
    
        if (backgroundImageFile) {
            formData.append('backgroundImage', backgroundImageFile);
        } else if (existingBackgroundImage) {
            formData.append('existingBackgroundImage', existingBackgroundImage);
        }
    
        try {
            await axios.put(`${API_URL}/api/cards/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            alert('Card information updated!');
            navigate('/cards');
        } catch (error) {
            console.error('Error updating card information:', error);
            setError('Error updating card information');
        }
    };
    

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_URL}/api/cards/${id}`);
            alert('Card deleted!');
            navigate('/cards');
        } catch (error) {
            console.error('Error deleting card:', error);
            setError('Error deleting card');
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(`${process.env.REACT_APP_FRONTEND_URL}/card/${cardData.uuid}`)
            .then(() => {
                alert('Link copied to clipboard!');
            })
            .catch(err => {
                console.error('Error copying link:', err);
            });
    };

    const handleDownloadPDF = async () => {
        const cardPreview = document.querySelector('.card-preview-unique');

        if (!cardData.backgroundImage) {
            cardPreview.querySelector('.card-preview-top-unique').style.backgroundColor = cardData.backgroundColor;
        } else {
            cardPreview.querySelector('.card-preview-top-unique').style.backgroundColor = 'transparent';
        }

        const canvas = await html2canvas(cardPreview, {
            scale: 2,
            useCORS: true,
        });

        const imgData = canvas.toDataURL('image/png');

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: [225, 300],
        });

        pdf.addImage(imgData, 'PNG', 0, 0, 225, 300);
        pdf.save('card-preview.pdf');
    };

    const handleIconClick = (url) => {
        window.open(url, '_blank');
    };

    return (
        <div className="card-form-container-unique">
            <h1 className="edit-workspace-title">Edit Digital Identity</h1>
            {error && <p className="error-message">{error}</p>}
            
            <div className="form-and-preview-unique">
                {/* Left Side: Mockup Frame Display and Quick Actions Row */}
                <div className="preview-and-qr-unique">
                    <div className="card-preview-unique">
                        <div 
                            className="card-preview-top-unique"
                            style={{
                                backgroundColor: !cardData.backgroundImage ? cardData.backgroundColor : 'transparent',
                                backgroundImage: cardData.backgroundImage ? `url(${cardData.backgroundImage})` : 'none',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'cover',
                            }}
                        />
                        <div className="card-preview-bottom-unique">
                            {/* Headline Branding */}
                            <h3>{cardData.name || 'Your Name'}</h3>
                            <p className="preview-headline-title">{cardData.position || 'Position Title'}</p>
                            <p className="preview-headline-company">{cardData.companyName || 'Company Name'}</p>
                            
                            <div className="preview-divider" />

                            {/* Contact Grid Rows with inline aligned Left-hand side Icons */}
                            <div className="contact-rows-stack">
                                {cardData.phone && (
                                    <div className="contact-row-item" onClick={() => handleIconClick(`tel:${cardData.phone}`)}>
                                        <FaPhoneAlt className="row-icon" /> <span>{cardData.phone}</span>
                                    </div>
                                )}
                                {cardData.email && (
                                    <div className="contact-row-item" onClick={() => handleIconClick(`mailto:${cardData.email}`)}>
                                        <FaEnvelope className="row-icon" /> <span>{cardData.email}</span>
                                    </div>
                                )}
                                {cardData.address && (
                                    <div className="contact-row-item" onClick={() => handleIconClick(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cardData.address)}`)}>
                                        <FaMapMarkerAlt className="row-icon" /> <span>{cardData.address}</span>
                                    </div>
                                )}
                                {cardData.website && (
                                    <div className="contact-row-item" onClick={() => handleIconClick(cardData.website.startsWith('http') ? cardData.website : `https://${cardData.website}`)}>
                                        <FaGlobe className="row-icon" /> <span>{cardData.website}</span>
                                    </div>
                                )}
                            </div>
                            
                            <hr className="preview-divider" />

                            {/* Social Media Link Connect Section */}
                            <div className="social-icons-unique">
                                {cardData.linkedin && <FaLinkedin className="icon" onClick={() => handleIconClick(cardData.linkedin)} />}
                                {cardData.facebook && <FaFacebook className="icon" onClick={() => handleIconClick(cardData.facebook)} />}
                                {cardData.instagram && <FaInstagram className="icon" onClick={() => handleIconClick(cardData.instagram)} />}
                                {cardData.youtube && <FaYoutube className="icon" onClick={() => handleIconClick(cardData.youtube)} />}
                                {cardData.twitter && <FaTwitter className="icon" onClick={() => handleIconClick(cardData.twitter)} />}
                                {cardData.github && <FaGithub className="icon" onClick={() => handleIconClick(cardData.github)} />}
                                {cardData.zoom && <FaVideo className="icon" onClick={() => handleIconClick(cardData.zoom)} />}
                                {cardData.teams && <BsMicrosoftTeams className="icon" onClick={() => handleIconClick(cardData.teams)} />}
                            </div>
                        </div>
                    </div>

                    {/* Quick Access Action Deck & Live Asset QR */}
                    <div className="qr-code-unique">
                        <div className="qr-image-wrapper">
                            {cardData.qrCode ? <img src={cardData.qrCode} alt="Identity Badge QR Link" /> : <div className="qr-placeholder" />}
                        </div>
                        <div className="qr-action-buttons">
                            <button type="button" className="action-btn secondary-blue" onClick={handleCopyLink}>Copy Card Link</button>
                            <button type="button" className="action-btn secondary-slate" onClick={handleDownloadPDF}>Download PDF</button>
                        </div>
                    </div>
                </div>

                {/* Right Side: Tabbed Workspace Controls (Dashboard Style Input Box Container) */}
                <form onSubmit={handleSubmit} className="card-edit-form-unique">
                    <div className="tabs-unique">
                        <button
                            type="button"
                            className={activeTab === 'Information' ? 'active' : ''}
                            onClick={() => setActiveTab('Information')}
                        >
                            Information
                        </button>
                        <button
                            type="button"
                            className={activeTab === 'Social Links' ? 'active' : ''}
                            onClick={() => setActiveTab('Social Links')}
                        >
                            Social Links
                        </button>
                        <button
                            type="button"
                            className={activeTab === 'Customization' ? 'active' : ''}
                            onClick={() => setActiveTab('Customization')}
                        >
                            Customization
                        </button>
                    </div>

                    <div className="workspace-fields-panel">
                        {activeTab === 'Information' && (
                            <div className="tab-content-unique">
                                <label>
                                    Full Name
                                    <input type="text" name="name" placeholder="Alex Morgan" value={cardData.name} onChange={handleChange} />
                                </label>
                                <label>
                                    Job Title
                                    <input type="text" name="position" placeholder="Lead Project Manager" value={cardData.position} onChange={handleChange} />
                                </label>
                                <label>
                                    Company Name
                                    <input type="text" name="companyName" placeholder="Acme Corporation" value={cardData.companyName} onChange={handleChange} />
                                </label>
                                <label>
                                    Phone Number
                                    <input type="text" name="phone" placeholder="+44 7700 900077" value={cardData.phone} onChange={handleChange} />
                                </label>
                                <label>
                                    Email Address
                                    <input type="email" name="email" placeholder="alex@acme.com" value={cardData.email} onChange={handleChange} />
                                </label>
                                <label>
                                    Office Location Address
                                    <input type="text" name="address" placeholder="London, United Kingdom" value={cardData.address} onChange={handleChange} />
                                </label>
                                <label>
                                    Corporate Website Link
                                    <input type="text" name="website" placeholder="www.acme.com" value={cardData.website} onChange={handleChange} />
                                </label>
                            </div>
                        )}


                        {activeTab === 'Social Links' && (
                            <div className="tab-content-unique">
                                <label>
                                    LinkedIn URL
                                    <input type="url" name="linkedin" placeholder="https://linkedin.com/in/username" value={cardData.linkedin} onChange={handleChange} />
                                </label>
                                <label>
                                    Facebook URL
                                    <input type="url" name="facebook" placeholder="https://facebook.com/profile" value={cardData.facebook} onChange={handleChange} />
                                </label>
                                <label>
                                    Instagram URL
                                    <input type="url" name="instagram" placeholder="https://instagram.com/username" value={cardData.instagram} onChange={handleChange} />
                                </label>
                                <label>
                                    YouTube Channel URL
                                    <input type="url" name="youtube" placeholder="https://youtube.com/c/channel" value={cardData.youtube} onChange={handleChange} />
                                </label>
                                <label>
                                    Twitter URL
                                    <input type="url" name="twitter" placeholder="https://twitter.com/username" value={cardData.twitter} onChange={handleChange} />
                                </label>
                                <label>
                                    GitHub Profile URL
                                    <input type="url" name="github" placeholder="https://github.com/username" value={cardData.github} onChange={handleChange} />
                                </label>
                                <label>
                                    Zoom Meeting Room URL
                                    <input type="url" name="zoom" placeholder="https://zoom.us/j/meetingid" value={cardData.zoom} onChange={handleChange} />
                                </label>
                                <label>
                                    Microsoft Teams Link
                                    <input type="url" name="teams" placeholder="https://teams.microsoft.com/l/meetup" value={cardData.teams} onChange={handleChange} />
                                </label>
                            </div>
                        )}

                        {activeTab === 'Customization' && (
                            <div className="tab-content-unique">
                                <label className="color-picker-row-label">
                                    Primary Background Accent Color
                                    <div className="color-picker-input-wrapper">
                                        <input type="color" name="backgroundColor" value={cardData.backgroundColor} onChange={handleChange} />
                                        <span className="hex-color-preview">{cardData.backgroundColor}</span>
                                    </div>
                                </label>
                                <div className="embedded-upload-zone">
                                    <span className="upload-zone-label">Card Canvas Header Image Background</span>
                                    <input type="file" accept="image/*" onChange={handleBackgroundImageChange} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Operational Action Footer Bars */}
                    <div className="form-action-deck">
                        <button type="submit" className="save-submit-btn">Update Digital Card</button>
                        <button type="button" onClick={handleDelete} className="delete-submit-btn">Delete Card Profile</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditCard;
